// Copyright (c) Docugami, Inc. All rights reserved.

export enum StorageKey {
    TelemetryClientLocalSessionId = 'TelemetryClientLocalSessionId',
    TelemetryClientGlobalSessionId = 'TelemetryClientGlobalSessionId',

    LastOrganizationId = 'LastOrganizationId',

    DebugLoadDelay = 'DebugLoadDelay',
    ActiveLongDownloads = 'ActiveLongDownloads',

    CoachMarkLookup = 'CoachMarkLookup',

    StorageProviderUploads = 'StorageProviderUploads',
    RecentLabels = 'RecentLabels',

    NumberOfSuggestedChunksInDemoMode = 'NumberOfSuggestedChunksInDemoMode',

    SelfServiceAccessCode = 'SelfServiceAccessCode',

    LastActiveUpdateTime = 'LastActiveUpdateTime',
    LastActiveTime = 'LastActiveTime',

    FirstArrival = "FirstArrival",
    DefaultProjectId = 'DefaultProjectId',

    ReportsChatWidth = 'ReportsChatWidth',
    ReportsChatCollapsed = 'ReportsChatCollapsed',
    ExtractionModel = 'ExtractionModel',
}

/**
 * Helper for interacting with window storage
 */
export class StorageAccessor {
    private readonly _storage: Storage;
    private readonly _prefix: string;

    constructor(storage: Storage = window.localStorage, prefix: string = 'docugami.') {
        this._storage = storage;
        this._prefix = prefix;
    }

    getNumber(key: string, defaultValue: number = -1): number {
        const item = this.getItem(key);
        const value = !!item ? parseInt(item) : defaultValue;
        return value;
    }

    setNumber(key: string, value: number): void {
        this.setItem(key, value.toString());
    }

    getBoolean(key: string): boolean | undefined;
    getBoolean(key: string, defaultValue: boolean): boolean;

    getBoolean(key: string, defaultValue?: boolean): boolean | undefined {
        const item = this.getItem(key);
        const value = !!item ? item === String(true) : defaultValue;
        return value;
    }

    setBoolean(key: string, value: boolean): void {
        this.setItem(key, value.toString());
    }

    getString(key: string, defaultValue: string | null = null) {
        const item = this.getItem(key);
        const value = !!item ? item : defaultValue;
        return value;
    }

    setString(key: string, value: string): void {
        this.setItem(key, value);
    }

    getDate(key: string, defaultValue: Date | null = null) {
        const item = this.getString(key);
        const value = !!item ? new Date(item) : defaultValue;
        return value;
    }

    setDate(key: string, value: Date): void {
        this.setString(key, value.toISOString());
    }

    getObject<T>(key: string, defaultValue: T | null = null): T | null {
        const item = this.getItem(key);
        const value: T | null = !!item ? JSON.parse(item) : defaultValue;
        return value;
    }

    setObject(key: string, value: any): void {
        this.setItem(key, JSON.stringify(value));
    }

    removeKey(key: string): void {
        this._storage.removeItem(this._prefix + key);
    }

    private getItem(key: string): string | null {
        const qualifiedKey = this._prefix + key;
        return this._storage.getItem(qualifiedKey);
    }

    private setItem(key: string, value: string) {
        const qualifiedKey = this._prefix + key;
        this._storage.setItem(qualifiedKey, value);
    }
}
